import axios from 'axios';

// API base URL from environment variable
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => {
    // Transform product data to match frontend expectations
    // Handle paginated list responses (old format: data.data.data, new format: data.data)
    if (response.data?.data?.data && Array.isArray(response.data.data.data)) {
      // Old paginated format: { success, data: { data: [...], pagination: {...} } }
      response.data.data.data = response.data.data.data.map(transformProduct);
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      // New paginated format: { success, data: { data: [...], pagination: {...} } } - same as old for lists
      response.data.data = response.data.data.map(transformProduct);
    } else if (response.data?.data && typeof response.data.data === 'object' && response.data.data.npkN !== undefined) {
      // Single product response (new format): { success, data: { ...product } }
      response.data.data = transformProduct(response.data.data);
    } else if (response.data?.data?.data && typeof response.data.data.data === 'object' && response.data.data.data.npkN !== undefined) {
      // Single product response (old format): { success, data: { data: { ...product } } }
      response.data.data.data = transformProduct(response.data.data.data);
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login if unauthorized
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    return Promise.reject(error);
  }
);

// Transform product data from backend format to frontend format
const transformProduct = (product) => {
  if (!product) return product;

  // Parse micros if it's a JSON string
  let parsedMicros = product.micros;
  if (typeof product.micros === 'string') {
    try {
      parsedMicros = JSON.parse(product.micros);
    } catch (e) {
      parsedMicros = [];
    }
  }

  // Parse sizes if it's a JSON string
  let parsedSizes = product.sizes;
  if (typeof product.sizes === 'string') {
    try {
      parsedSizes = JSON.parse(product.sizes);
    } catch (e) {
      parsedSizes = [{ weight: '1 kg', price: 0 }];
    }
  }

  return {
    ...product,
    npk: {
      n: product.npkN || 0,
      p: product.npkP || 0,
      k: product.npkK || 0,
    },
    micros: parsedMicros || [],
    sizes: parsedSizes && parsedSizes.length > 0 ? parsedSizes : [
      { weight: '1 kg', price: 0 }
    ],
  };
};

// Products API
export const productsAPI = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/products', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  getFeatured: async (limit = 3) => {
    try {
      const response = await api.get('/products/featured', { params: { limit } });
      return response.data;
    } catch (error) {
      console.error('Error fetching featured products:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/products/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  },

  getBySlug: async (slug) => {
    try {
      const response = await api.get(`/products/slug/${slug}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  },

  create: async (data) => {
    try {
      const response = await api.post('/products', data);
      return response.data;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const response = await api.put(`/products/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/products/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  },
};

// Events API
export const eventsAPI = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/events', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  },

  getUpcoming: async (limit = 5) => {
    try {
      const response = await api.get('/events/upcoming', { params: { limit } });
      return response.data;
    } catch (error) {
      console.error('Error fetching upcoming events:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/events/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching event:', error);
      throw error;
    }
  },

  getBySlug: async (slug) => {
    try {
      const response = await api.get(`/events/slug/${slug}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching event:', error);
      throw error;
    }
  },

  create: async (data) => {
    try {
      const response = await api.post('/events', data);
      return response.data;
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const response = await api.put(`/events/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/events/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  },
};

// Contact API
export const contactAPI = {
  submit: async (data) => {
    try {
      const response = await api.post('/contact', data);
      return response.data;
    } catch (error) {
      console.error('Error submitting contact form:', error);
      throw error;
    }
  },
};

// Auth API
export const authAPI = {
  register: async (data) => {
    try {
      const response = await api.post('/auth/register', data);
      if (response.data.success && response.data.data.token) {
        localStorage.setItem('token', response.data.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
      }
      return response.data;
    } catch (error) {
      console.error('Error registering:', error);
      throw error;
    }
  },

  login: async (data) => {
    try {
      const response = await api.post('/auth/login', data);
      if (response.data.success && response.data.data.token) {
        localStorage.setItem('token', response.data.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.data.user));
      }
      return response.data;
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  getMe: async () => {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  getUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.post('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      return response.data;
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  },
};

// Team API
export const teamAPI = {
  getAll: async (params = {}) => {
    try {
      const response = await api.get('/team', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching team members:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/team/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching team member:', error);
      throw error;
    }
  },

  create: async (data) => {
    try {
      const response = await api.post('/team', data);
      return response.data;
    } catch (error) {
      console.error('Error creating team member:', error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const response = await api.put(`/team/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating team member:', error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/team/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting team member:', error);
      throw error;
    }
  },
};

// Users API
export const usersAPI = {
  getAll: async () => {
    try {
      const response = await api.get('/users');
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  getById: async (id) => {
    try {
      const response = await api.get(`/users/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  },

  create: async (data) => {
    try {
      const response = await api.post('/users', data);
      return response.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  update: async (id, data) => {
    try {
      const response = await api.put(`/users/${id}`, data);
      return response.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  delete: async (id) => {
    try {
      const response = await api.delete(`/users/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  toggleStatus: async (id) => {
    try {
      const response = await api.patch(`/users/${id}/toggle-status`);
      return response.data;
    } catch (error) {
      console.error('Error toggling user status:', error);
      throw error;
    }
  },
};

export default api;